/*
 * Que foto adelanta el HTML de cada ruta.
 *
 * Hermano de primeraFoto.ts, y la razon es la misma: de los 2302 ms de LCP de
 * la portada, 1282 eran "Load Delay" --el rato hasta que el navegador se entera
 * de la URL de la foto grande--. Aquel resuelve al visitante que vuelve,
 * leyendo lo que apunto la vez anterior en localStorage. Este resuelve al que
 * entra por primera vez: el build pregunta a Supabase cual es la primera foto
 * y la escribe como <link rel="preload"> en el index.html de esa ruta, asi que
 * el navegador la pide al leer la cabecera, antes incluso de bajar el bundle.
 *
 * Aqui solo viven funciones puras sobre filas ya traidas: la peticion la hace
 * el plugin de vite.config.ts, que es quien tiene las credenciales.
 *
 * Caduca, y es asumible: si publicas un post despues del ultimo despliegue, el
 * HTML adelanta la foto anterior --una peticion de ~50 KB tirada-- hasta la
 * siguiente reconstruccion. Medido el 22/10/2026, publicas uno cada 53 h de
 * mediana, asi que con reconstruir a diario esa ventana es corta.
 */

import { isVideoUrl, parseBlogImages } from "./contentModels";
import { galleryImageUrl, thumbnailImageUrl } from "./imageCdn";

/** Lo minimo que necesitamos de `feed_items`, en el orden en que llega. */
export type FilaDeConcierto = {
  post_id: string | null;
  archivo: string | null;
  item_index: number | null;
};

export type FilaDeArticulo = { imagenes: string | null };

/**
 * Recorta las filas a las de los primeros `posts` posts, enteros. Horneamos
 * estas filas en el HTML y cada byte se paga en cada visita, asi que no vale
 * un "las primeras 40 y ya": o un post entra con todas sus fotos o no entra,
 * porque media galeria pintaria mal hasta que llegue la consulta de verdad.
 */
export function filasDeLosPrimerosPosts<T extends { post_id: string | null }>(
  filas: readonly T[],
  posts: number,
): T[] {
  const vistos = new Set<string>();
  const salida: T[] = [];
  for (const fila of filas) {
    if (!fila.post_id) continue;
    if (!vistos.has(fila.post_id)) {
      if (vistos.size === posts) break;
      vistos.add(fila.post_id);
    }
    salida.push(fila);
  }
  return salida;
}

/**
 * La portada y el feed agrupan las filas por `post_id` conservando el orden de
 * la consulta (`fecha_utc desc, id desc`) y ordenan las fotos de cada post por
 * `item_index`. Replicamos eso y nos quedamos con la primera foto del primer
 * post. Si ese post abre con un video no hay nada que adelantar: un video no
 * se precarga como imagen.
 */
export function primeraFotoDeConciertos(
  filas: readonly FilaDeConcierto[],
  variante: "miniatura" | "galeria",
): string | null {
  const primerPost = filas.find((f) => f.post_id)?.post_id;
  if (!primerPost) return null;

  const delPost = filas
    .filter((f) => f.post_id === primerPost && f.archivo)
    .sort((a, b) => (a.item_index ?? 0) - (b.item_index ?? 0));

  const portada = delPost[0]?.archivo;
  if (!portada || isVideoUrl(portada)) return null;
  return variante === "galeria" ? galleryImageUrl(portada) : thumbnailImageUrl(portada);
}

/**
 * `/articulos` pinta la primera imagen de la primera fila, con las filas ya
 * ordenadas por `created_at desc`. Las dos vistas de esa pantalla piden la
 * miniatura, asi que no depende de cual tenga puesta el visitante.
 */
export function primeraFotoDeArticulos(filas: readonly FilaDeArticulo[]): string | null {
  const portada = parseBlogImages(filas[0]?.imagenes ?? null)[0];
  if (!portada || isVideoUrl(portada)) return null;
  return thumbnailImageUrl(portada);
}

/**
 * La etiqueta que se inyecta en la cabecera. `fetchpriority="high"` porque es
 * justo la imagen por la que se mide el LCP.
 */
export function etiquetaDePreload(url: string): string {
  const escapada = url.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  return `<link rel="preload" as="image" fetchpriority="high" href="${escapada}" />`;
}

/**
 * El `<script>` con los datos que la pantalla necesita para pintar sin esperar
 * a su consulta. Las claves son las mismas con las que las paginas leen la
 * cache (`cache:...`), asi que nadie tiene que cambiar nada para aprovecharlo:
 * su `initialData` ya pasa por ahi.
 *
 * `<` escapado porque un `</script>` dentro del JSON --en la descripcion de un
 * post, por ejemplo-- cerraria la etiqueta antes de tiempo y el resto del JSON
 * se pintaria como texto en la pagina.
 */
/**
 * La clave con la que una pagina de detalle (`/post/<tipo>/<id>`) encuentra lo
 * que el build dejo escrito. La usan los dos lados, por eso vive aqui: si se
 * separan, el horneado deja de encontrarse y nadie se entera, porque la pagina
 * seguiria funcionando --solo que esperando a la consulta, como antes--.
 */
export function clavePostDetalle(tipo: string, id: string): string {
  return `cache:post-${tipo}-${id}`;
}

export function etiquetaDeDatos(datos: Record<string, unknown>): string {
  if (Object.keys(datos).length === 0) return "";
  const json = JSON.stringify(datos).replace(/</g, "\\u003c");
  return `<script>window.__CACHE_HORNEADA__=${json}</script>`;
}
